import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMobile } from "@krea/customer-features/hooks";
import SVG from "react-inlinesvg";

import { useGCBankingData } from "@krea/customer-features/hooks/useGCBankingData";
import BankIdSVG from "@krea/customer-features/images/svg/bankid/bankid-colorless.svg";
import { settings } from "@krea/customer-features/settings";
import Button from "@krea/customer-features/shared-components/button";
import { Error } from "@krea/customer-features/shared-components/fields/error";
import TextInput from "@krea/customer-features/shared-components/fields/input";
import Preloader from "@krea/customer-features/shared-components/preloader";
import { Text } from "@krea/customer-features/shared-components/text";
import { IInstitution } from "@krea/customer-features/store/banking-data/types";
import twMerge from "@krea/customer-features/utils/tailwind-utils";
import CheckBox from "@krea/shared/design-system/fields/Checkbox";
import { AppModal } from "@krea/shared/design-system/modal";
import { CountryCode } from "@krea/shared/types/common";
import { IBankingDataFRSubmittedData } from "@krea/shared/types/fieldRequest";

import { CheckMark } from "../icon";

const StatusMessage = ({
  className,
  text,
}: {
  className?: string;
  text?: string | null;
}) => {
  return (
    <div className={twMerge("tw-flex tw-items-center", className)}>
      <CheckMark />

      <Text size="sm" className="tw-font-bold">
        {text}
      </Text>
    </div>
  );
};

const InstitutionItem = ({
  institution,
  onInstitutionClick,
  isMobile,
}: {
  institution: IInstitution;
  onInstitutionClick: (institutionId: string) => void;
  isMobile: boolean;
}) => {
  return (
    <div
      aria-hidden
      key={institution.id}
      onClick={() => {
        onInstitutionClick(institution.id);
      }}
      className={twMerge(
        "tw-flex tw-cursor-pointer tw-flex-row tw-items-center tw-justify-between tw-rounded tw-border tw-border-gray-200 tw-bg-gray-100 tw-p-4 hover:tw-bg-gray-200",
        {
          "tw-h-[80px] tw-mb-2": !isMobile,
          "tw-h-[50px] tw-mb-3": isMobile,
        },
      )}
    >
      <Text size={isMobile ? "md" : "lg"} className="tw-font-bold">
        {institution.name}
      </Text>
      <img
        className={twMerge({
          "tw-w-[60px]": !isMobile,
          "tw-w-[30px]": isMobile,
        })}
        src={institution.logo}
        alt={institution.name}
      />
    </div>
  );
};

const BankSelection = ({
  institutions,
  isLoading,
  onInstitutionClick,
}: {
  institutions?: IInstitution[];
  isLoading: boolean;
  onInstitutionClick: (institutionId: string) => void;
}) => {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const [searchQuery, setSearchQuery] = useState("");

  const [matchedInstitutions, setMatchedInstitutions] = useState<
    IInstitution[] | undefined
  >([]);

  useEffect(() => {
    if (searchQuery.length === 0) {
      setMatchedInstitutions(undefined);
    }
  }, [searchQuery]);

  const recommendedInstitutions = institutions?.filter(
    (it) => it.isRecommended,
  );

  const nonRecommendedInstitutions = institutions?.filter(
    (it) => !it.isRecommended,
  );

  const [allInstitutionsShown, setAllInstitutionsShown] = useState(false);

  const [shownInstitutions, setShownInstitutions] = useState(
    institutions?.filter((it) => it.isRecommended),
  );

  useEffect(() => {
    if (!allInstitutionsShown) {
      setShownInstitutions(institutions?.filter((it) => it.isRecommended));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutions]);

  const loadAdditionalInstitutions = async () => {
    setShownInstitutions(
      recommendedInstitutions && nonRecommendedInstitutions
        ? recommendedInstitutions.concat(nonRecommendedInstitutions)
        : [],
    );
    setAllInstitutionsShown(true);
  };

  const resetInstitutions = () => {
    setAllInstitutionsShown(false);
    setShownInstitutions(recommendedInstitutions);
    setSearchQuery("");
    setMatchedInstitutions(undefined);
  };

  return (
    <>
      {isLoading ? (
        <Preloader size="md" className="" />
      ) : (
        <div>
          <div
            className={twMerge("tw-mb-3 tw-text-center", {
              "tw-mx-6": !isMobile,
            })}
          >
            <Text>{t("openBankingData.selectRevenueBank")}</Text>

            <div className="tw-mt-3">
              <TextInput
                className="tw-rounded tw-border tw-border-solid tw-border-primary"
                id="banksearch"
                placeholder="Search"
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);

                  if (e.target.value !== "") {
                    setMatchedInstitutions(
                      institutions?.filter((it) =>
                        it.name
                          .trim()
                          .toLowerCase()
                          .startsWith(e.target.value.trim().toLowerCase()),
                      ),
                    );
                  } else {
                    setMatchedInstitutions(undefined);
                  }
                }}
              />
            </div>
          </div>
          <div className={twMerge({ "tw-px-6": !isMobile })}>
            {searchQuery
              ? matchedInstitutions?.map((institution) => (
                  <InstitutionItem
                    key={institution.id}
                    institution={institution}
                    onInstitutionClick={() => {
                      onInstitutionClick(institution.id);
                      resetInstitutions();
                    }}
                    isMobile={isMobile}
                  />
                ))
              : shownInstitutions?.map((institution) => (
                  <InstitutionItem
                    key={institution.id}
                    institution={institution}
                    onInstitutionClick={() => {
                      onInstitutionClick(institution.id);
                      resetInstitutions();
                    }}
                    isMobile={isMobile}
                  />
                ))}
          </div>

          {!allInstitutionsShown && !searchQuery && (
            <div className="tw-mt-3 tw-flex tw-items-center tw-justify-center">
              <Button
                variant="outlined"
                size={isMobile ? "md" : "lg"}
                className={twMerge("tw-w-full", {
                  "tw-mx-6": !isMobile,
                  "tw-mb-10": isMobile,
                })}
                onClick={loadAdditionalInstitutions}
              >
                {t("openBankingData.showAllBanks")}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

interface IBankingDataProps {
  resourceId: string;
  resourceType: string;
  className?: string;
  isOnboarded: boolean;
  onSuccess?: () => void;
  isPrivatePerson?: boolean;
  lightTheme?: boolean;
  isGuarantorsForm?: boolean;
  selectedAccounts?: IBankingDataFRSubmittedData["structured_data"];
  postSelectedAccounts?: (
    selectedAccounts: IBankingDataFRSubmittedData["structured_data"],
  ) => void;
}

const BankingData = ({
  resourceId,
  resourceType,
  onSuccess,
  className,
  isOnboarded,
  isPrivatePerson,
  lightTheme,
  isGuarantorsForm = false,
  selectedAccounts,
  postSelectedAccounts,
}: IBankingDataProps) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const isSweden = settings.countryCode === CountryCode.SE;

  const [hasStarted, setHasStarted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const [showOnboarding, setShowOnboarding] = useState(isOnboarded);

  const [selectedAccountsToPost, setSelectedAccountsToPost] =
    useState(selectedAccounts);

  const [institutionToEdit, setInstitutionToEdit] = useState<
    IInstitution | undefined
  >(undefined);

  const onSuccessEvent = () => {
    setIsInitialized(false);

    if (isPrivatePerson) {
      setHasStarted(false);
    }

    setShowOnboarding(false);

    if (onSuccess) {
      onSuccess();
    }
  };

  const { institutionsQuery, initialize, reset, isError, isCancelled } =
    useGCBankingData({ resourceId, resourceType, onSuccess: onSuccessEvent });

  useEffect(() => {
    if (isError ?? isCancelled) {
      if (isPrivatePerson) {
        setIsInitialized(false);
        setHasStarted(false);
      }

      reset(!isError);
    }
  }, [isError, isCancelled]);

  useEffect(() => {
    if (isOnboarded && isPrivatePerson) {
      setHasStarted(false);
      setIsInitialized(false);
    }
  }, [isOnboarded, isPrivatePerson]);

  const onClickHandler = async () => {
    setHasStarted(true);

    if (!isPrivatePerson) {
      setShowOnboarding(true);
    }
  };

  const onInstitutionClick = async (institutionId: string) => {
    setIsInitialized(true);
    initialize(institutionId);

    if (!isPrivatePerson) {
      const institutionMatch = institutionsQuery.data?.find(
        (it) => it.id === institutionId,
      );

      setInstitutionToEdit(institutionMatch);
    }
  };

  interface BankAccount {
    bank_account_id: string;
    bank_account_name: string;
    available_amount?: number | null;
    explicitly_selected_by_user: boolean;
    bank_name: string;
  }

  const onEdit = (institutionName: string) => {
    const institutionMatch = institutionsQuery.data?.find(
      (it) => it.name === institutionName,
    );
    setInstitutionToEdit(institutionMatch);

    setHasStarted(true);
    setShowOnboarding(false);
  };

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const onConfirmRevenueAccounts = () => {
    if (
      postSelectedAccounts &&
      selectedAccountsToPost &&
      selectedAccountsToPost.filter((acc) => acc.explicitly_selected_by_user)
        .length > 0
    ) {
      postSelectedAccounts(selectedAccountsToPost);
      setHasStarted(false);
    } else {
      setErrorMessage(t("openBankingData.selectAtLeastOneAccount"));
    }
  };

  const shareAnotherBankStatement = () => {
    setShowOnboarding(true);
    setHasStarted(true);
  };

  const formatBankBalance = (balance?: number | null) => {
    const countryCode = settings.countryCode;
    const balanceMap = {
      [CountryCode.SE]: balance?.toLocaleString("sv-SE"),
      [CountryCode.FI]: balance?.toLocaleString("fi-FI"),
    };
    const balanceFormatted = balanceMap[countryCode];

    return balanceFormatted ?? balance;
  };

  useEffect(() => {
    if (!isPrivatePerson) {
      setSelectedAccountsToPost(selectedAccounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccounts]);

  useEffect(() => {
    if (!isPrivatePerson) {
      if (
        (selectedAccountsToPost?.filter(
          (acc) => acc.explicitly_selected_by_user,
        ).length ?? 0) > 0
      ) {
        setErrorMessage(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountsToPost]);

  const groupedByBank = selectedAccounts
    ? selectedAccounts.reduce<
        Record<
          string,
          IBankingDataFRSubmittedData["structured_data"] | undefined
        >
      >((acc, item) => {
        const bankName = item.bank_name ?? "";

        if (!acc[bankName]) {
          acc[bankName] = [];
        }

        acc[bankName]?.push(item);

        return acc;
      }, {})
    : null;

  return isPrivatePerson ? (
    <div className={twMerge(className)}>
      <div
        className={twMerge("xl:tw-px-5", {
          "tw-bg-white": lightTheme,
          "tw-bg-gray": !lightTheme && !isGuarantorsForm,
          "tw-rounded tw-p-3 xl:tw-px-5 xl:tw-py-4": !isGuarantorsForm,
          "px-5": isGuarantorsForm,
        })}
      >
        <>
          {isGuarantorsForm && !isOnboarded && (
            <div className="tw-mb-1 tw-mt-3 tw-flex">
              <Text size="sm">
                {t("openBankingData.isPrivate.optional")}
                <a
                  aria-hidden
                  style={{ cursor: "pointer" }}
                  className="tw-font-bold tw-text-primary"
                  onClick={onClickHandler}
                >
                  {t("openBankingData.isPrivate.title")}
                </a>
              </Text>
            </div>
          )}
          {!isGuarantorsForm && (
            <Text size="sm" className="tw-mb-3 tw-font-bold">
              {t("openBankingData.isPrivate.title")}
            </Text>
          )}

          {isOnboarded && (
            <StatusMessage
              className="tw-pt-1"
              text={t("openBankingData.success")}
            />
          )}

          {!isOnboarded && !isGuarantorsForm && (
            <Button variant="outlined" size="sm" onClick={onClickHandler}>
              {t("openBankingData.isPrivate.cta")}
            </Button>
          )}
        </>
      </div>

      <AppModal
        onClose={() => {
          reset();
          setHasStarted(false);
          setIsInitialized(false);
        }}
        title={t("openBankingData.chooseBank")}
        open={hasStarted}
        className="tw-max-h-[70vh] tw-min-h-[200px]"
        setOpen={() => {}}
      >
        <BankSelection
          institutions={institutionsQuery.data}
          isLoading={institutionsQuery.isFetching || isInitialized}
          onInstitutionClick={onInstitutionClick}
        />
      </AppModal>

      {isError && (
        <Text
          size="sm"
          className="tw-ml-5 tw-mt-2 tw-font-bold tw-text-red-600"
        >
          {t("openBankingData.error")}
        </Text>
      )}
    </div>
  ) : (
    // New banking data flow with bank account selection
    <>
      {isEmpty(groupedByBank) && !isGuarantorsForm && (
        <div>
          <Text
            size="sm"
            className="tw-mb-4 tw-flex tw-flex-wrap tw-whitespace-pre-wrap tw-font-bold tw-text-orange"
          >
            {t("openBankingData.clickToConnectBank")}
          </Text>

          <Button
            variant="outlined"
            size="sm"
            className="tw-bg-white"
            onClick={onClickHandler}
            endIcon={isSweden ? <SVG src={BankIdSVG} /> : null}
          >
            {t("openBankingData.shareBankStatement")}
          </Button>
        </div>
      )}

      {!isGuarantorsForm && !isEmpty(groupedByBank) && (
        <div>
          <Text
            size="sm"
            className="tw-flex tw-flex-wrap tw-whitespace-pre-wrap"
          >
            {t("openBankingData.successfullyConnected")}
          </Text>

          {Object.keys(groupedByBank).map((bankName, index) => (
            <div
              key={`${bankName} ${index}`}
              className={twMerge(
                "tw-bg-mint-100 tw-rounded tw-my-3 tw-flex tw-justify-between",
                {
                  "tw-py-1 tw-px-[6px] md:tw-p-3":
                    (groupedByBank[bankName]?.filter(
                      (acc) => acc.explicitly_selected_by_user,
                    ).length ?? 0) > 1,
                  "tw-py-1 tw-px-[6px] md:tw-px-3 md:tw-pt-3 md:tw--pb-5":
                    groupedByBank[bankName]?.filter(
                      (acc) => acc.explicitly_selected_by_user,
                    ).length === 1,
                },
              )}
            >
              <div className="tw-h-full tw-w-full tw-space-y-4 lg:tw-space-y-0">
                {groupedByBank[bankName]
                  ?.filter((acc) => acc.explicitly_selected_by_user)
                  .map((account, index) => (
                    <div
                      key={index}
                      className="tw-w-full lg:tw-flex"
                      id={account.bank_account_id}
                    >
                      <div className="lg:tw-w-[33%] lg:tw-px-1">
                        <Text
                          size="sm"
                          className="tw-hyphens-auto tw-font-bold"
                        >
                          {bankName}
                        </Text>
                      </div>
                      <div className="lg:tw-w-[33%] lg:tw-px-1">
                        <Text
                          size="sm"
                          className="tw-hyphens-auto lg:tw-font-bold"
                        >
                          {account.bank_account_name}
                        </Text>
                      </div>
                      <div className="lg:tw-w-[33%] lg:tw-px-1">
                        <Text
                          size="sm"
                          className="tw-hyphens-auto lg:tw-font-bold"
                        >
                          {formatBankBalance(account.available_amount)}
                        </Text>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="tw-ml-1 tw-mr-0 tw-flex tw-space-x-2 tw-pt-[2px] md:tw-mr-2">
                <Button
                  variant="text"
                  size="sm"
                  onClick={() => onEdit(bankName)}
                  className="tw-items-start"
                >
                  {t("iaq.edit")}
                </Button>
              </div>
            </div>
          ))}

          <Button variant="text" size="sm" onClick={shareAnotherBankStatement}>
            {t("openBankingData.shareAnotherBankStatement")}
          </Button>
        </div>
      )}

      {!isGuarantorsForm && (
        <AppModal
          onClose={() => {
            reset();
            setHasStarted(false);
            setIsInitialized(false);
            setSelectedAccountsToPost(selectedAccounts);
          }}
          title={t("openBankingData.connectBank")}
          open={hasStarted}
          className={twMerge("tw-max-h-[70vh] tw-min-h-[200px]", {
            "tw-px-0": isMobile,
          })}
          setOpen={() => {}}
        >
          {showOnboarding ? (
            <BankSelection
              institutions={institutionsQuery.data}
              isLoading={institutionsQuery.isFetching || isInitialized}
              onInstitutionClick={onInstitutionClick}
            />
          ) : (
            <>
              <div className="tw-rounded tw-border tw-border-solid tw-border-gray">
                <div className="tw-flex tw-bg-mint-100 tw-p-4">
                  {institutionToEdit ? (
                    <img
                      className="tw-mr-4 tw-w-[35px]"
                      src={institutionToEdit.logo}
                      alt={institutionToEdit.name}
                    />
                  ) : null}

                  <Text
                    size={isMobile ? "lg" : "xxl"}
                    className="tw-mt-1 tw-font-bold"
                  >
                    {institutionToEdit?.name}
                  </Text>
                </div>
                <div className="tw-m-4">
                  <Text>{t("openBankingData.selectRevenueAccounts")}</Text>

                  <div className="tw-my-3 tw-space-y-5 tw-border tw-border-solid tw-border-gray tw-p-2">
                    {groupedByBank?.[institutionToEdit?.name ?? ""]?.map(
                      (account) => (
                        <div key={account.bank_account_id}>
                          <CheckBox
                            id={account.bank_account_id}
                            label={account.bank_account_name}
                            checked={
                              selectedAccountsToPost?.find(
                                (acc) =>
                                  acc.bank_account_id ===
                                  account.bank_account_id,
                              )?.explicitly_selected_by_user
                            }
                            onChange={(checked) => {
                              setSelectedAccountsToPost((prevAccounts) =>
                                prevAccounts?.map((acc) =>
                                  acc.bank_account_id ===
                                  account.bank_account_id
                                    ? {
                                        ...acc,
                                        explicitly_selected_by_user: checked,
                                      }
                                    : acc,
                                ),
                              );
                            }}
                          />
                          <Text size="sm" className="tw-ml-7">
                            {formatBankBalance(account.available_amount)}
                          </Text>
                        </div>
                      ),
                    )}
                  </div>
                </div>
                {errorMessage && (
                  <Error className="tw--mt-1 tw-mb-3 tw-ml-5">
                    {errorMessage}
                  </Error>
                )}
              </div>
              <Button
                size="lg"
                className="tw-mt-5 tw-w-full"
                onClick={onConfirmRevenueAccounts}
              >
                {t("openBankingData.confirmRevenueAccounts")}
              </Button>
            </>
          )}
        </AppModal>
      )}
    </>
  );
};

export default BankingData;
